import { Zap, Gauge, Clock } from "lucide-react";

// Shared "AI" pill styling for Jev-powered, fast-decision outputs.
const URGENCY_STYLES = {
  critical: "bg-rose-100 text-rose-700 border-rose-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-sky-100 text-sky-700 border-sky-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export function UrgencyBadge({ level, className = "" }) {
  if (!level) return null;
  const key = String(level).toLowerCase();
  const style = URGENCY_STYLES[key] || URGENCY_STYLES.low;
  return (
    <span
      data-testid={`jev-urgency-${key}`}
      title="Urgency scored instantly by Jev"
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${style} ${className}`}
    >
      <Zap className="w-3 h-3" strokeWidth={2.2} />
      {key} urgency
    </span>
  );
}

const TONE_STYLES = {
  Positive: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Neutral: "bg-slate-100 text-slate-600 border-slate-200",
  Tense: "bg-rose-100 text-rose-700 border-rose-200",
};

export function ToneBadge({ tone, className = "" }) {
  if (!tone) return null;
  const style = TONE_STYLES[tone] || TONE_STYLES.Neutral;
  return (
    <span
      data-testid={`jev-tone-${String(tone).toLowerCase()}`}
      title="Meeting tone tagged by Jev"
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${style} ${className}`}
    >
      <Gauge className="w-3 h-3" strokeWidth={2} />
      {tone} tone
    </span>
  );
}

// A segmented ribbon of the meeting split into time windows, each coloured by
// the agenda topic Jev assigned to it.
const RIBBON_COLORS = ["#6C5FBC", "#3D6FA8", "#3E7D5C", "#B06A2C", "#8B7FD4", "#B04A66", "#4C9AA8", "#9A6FB0"];

function fmtClock(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export function TopicTimeline({ timeline }) {
  if (!timeline || timeline.length === 0) return null;
  const topics = [...new Set((timeline || []).map((t) => t.topic))];
  const colorFor = (topic) => RIBBON_COLORS[topics.indexOf(topic) % RIBBON_COLORS.length];
  const total = (timeline || []).reduce((acc, t) => acc + Math.max(0, (t.end || 0) - (t.start || 0)), 0) || 1;

  return (
    <section
      className="yl-surface p-6"
      data-testid="recap-topic-timeline"
    >
      <div className="eyebrow mb-4 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" strokeWidth={1.8} /> Topic timeline
        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[9px] font-semibold tracking-normal normal-case text-muted-foreground">
          <Zap className="w-2.5 h-2.5" strokeWidth={2.2} /> Jev
        </span>
      </div>
      <div className="flex h-7 w-full overflow-hidden rounded-full border border-border">
        {(timeline || []).map((t, i) => {
          const w = (Math.max(0, (t.end || 0) - (t.start || 0)) / total) * 100;
          return (
            <div
              key={i}
              data-testid={`topic-window-${i}`}
              title={`${t.topic} · ${fmtClock(t.start)}–${fmtClock(t.end)}`}
              style={{ width: `${w}%`, background: colorFor(t.topic) }}
              className="h-full transition-opacity hover:opacity-80"
            />
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
        {topics.map((topic) => (
          <div key={topic} className="flex items-center gap-1.5 text-xs">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: colorFor(topic) }} />
            <span className="text-foreground/80">{topic}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
